import type { SupportedLocale } from "@/i18n";

/**
 * Traductions des pages d'authentification (login, 2FA, mot de passe oublié)
 * embarquées statiquement dans le bundle JS.
 *
 * Pourquoi : ces pages sont les toutes premières affichées et n'ont aucune
 * autre donnée à charger. Sans ces valeurs, elles devaient attendre le bundle
 * de traductions distant (~300 Ko + aller-retour réseau) avant de s'afficher,
 * d'où un long écran de chargement / un flash des clés brutes.
 *
 * Ces valeurs servent de DÉFAUT immédiat : dès que le bundle distant est chargé
 * (localStorage puis HTTP via i18next-chained-backend), ses valeurs écrasent
 * celles-ci (merge i18next, le backend gagne). Combiné à
 * `partialBundledLanguages: true` dans `i18n.ts`.
 *
 * Ne contient QUE les clés réellement utilisées par les pages d'auth. Certaines
 * (`login.error`, `login.invalidCredentials.title/description`, `step`) sont
 * absentes de la base : le défaut ci-dessous les rend enfin traduites.
 * `loginBackgroundImageUrl` est volontairement absent (image servie à distance).
 */
type AuthTranslations = Record<string, string>;

export const authFallbackResources: Record<SupportedLocale, AuthTranslations> = {
  fr: {
    loginBoxTitle: "Connexion",
    emailAddress: "Votre e-mail",
    password: "Mot de passe",
    connect: "Connexion",
    passwordForgotten: "Mot de passe oublié",
    verify: "Vérifier",
    step: "Étape",
    version: "Version ",
    copyrightInventoZen: "© InventoZen 2026",
    CGV: "CGV",
    footerLegalNotices: "Mentions légales",
    optimizeForChrome: "Site optimisé pour Google Chrome",
    titleOfTheWork: "Ils ont choisi InventoZen",
    artistFirstPage: "Jean MIOTTE",
    "login.twoFactorTitle": "Entrez le code",
    "login.twoFactorDescription":
      "Nous venons d'envoyer un code de vérification à votre adresse e-mail",
    "login.twoFactorExpired": "Le code a expiré",
    "login.twoFactorResendAsk": "Vous n'avez rien reçu ?",
    "login.twoFactorResendCTA": "Renvoyer le code",
    "login.invalid2FAToken": "Code invalide",
    "login.invalidCredentials": "Identifiant ou mot de passe invalide",
    "login.invalidCredentials.title": "Échec de la connexion",
    "login.invalidCredentials.description":
      "Identifiant ou mot de passe invalide",
    "login.success.title": "Connexion réussie",
    "login.success.description": " ",
    "login.error": "Une erreur est survenue",
  },
  en: {
    loginBoxTitle: "Login",
    emailAddress: "Your e-mail",
    password: "Password",
    connect: "Login",
    passwordForgotten: "Forgotten password",
    verify: "Verify",
    step: "Step",
    version: "Version",
    copyrightInventoZen: "© InventoZen 2026",
    CGV: "T&Cs",
    footerLegalNotices: "Legal and credits",
    optimizeForChrome: "Website optimized for Google Chrome",
    titleOfTheWork: "They chose InventoZen",
    artistFirstPage: "Jean MIOTTE",
    "login.twoFactorTitle": "Enter the code",
    "login.twoFactorDescription":
      "We have just sent a verification code to your e-mail address",
    "login.twoFactorExpired": "The code has expired",
    "login.twoFactorResendAsk": "You haven't received anything?",
    "login.twoFactorResendCTA": "Resend code",
    "login.invalid2FAToken": "Invalid code",
    "login.invalidCredentials": "Invalid username or password",
    "login.invalidCredentials.title": "Login failed",
    "login.invalidCredentials.description": "Invalid username or password",
    "login.success.title": "Login successful",
    "login.success.description": " ",
    "login.error": "An error occurred",
  },
  es: {
    loginBoxTitle: "Conexión",
    emailAddress: "Su e-mail",
    password: "Contraseña",
    connect: "Conexión",
    passwordForgotten: "¿Olvidó su contraseña?",
    verify: "Verificar",
    step: "Paso",
    version: "Versión",
    copyrightInventoZen: "© InventoZen 2026",
    CGV: "CGV",
    footerLegalNotices: "Aviso legal",
    optimizeForChrome: "Sitio optimizado para Google Chrome",
    titleOfTheWork: "Eligieron InventoZen",
    artistFirstPage: "Jean MIOTTE",
    "login.twoFactorTitle": "Introduzca el código",
    "login.twoFactorDescription":
      "Acabamos de enviar un código de verificación a su dirección de correo electrónico",
    "login.twoFactorExpired": "El código ha caducado",
    "login.twoFactorResendAsk": "¿No has recibido nada?",
    "login.twoFactorResendCTA": "Reenviar el código",
    "login.invalid2FAToken": "Código inválido",
    "login.invalidCredentials": "Identificador o contraseña inválidos",
    "login.invalidCredentials.title": "Error de conexión",
    "login.invalidCredentials.description":
      "Identificador o contraseña inválidos",
    "login.success.title": "Inicio de sesión exitoso",
    "login.success.description": " ",
    "login.error": "Se ha producido un error",
  },
  nl: {
    loginBoxTitle: "Inloggen",
    emailAddress: "Uw e-mail",
    password: "Wachtwoord",
    connect: "Inloggen",
    passwordForgotten: "Wachtwoord vergeten",
    verify: "Verifiëren",
    step: "Stap",
    version: "Versie",
    copyrightInventoZen: "© InventoZen 2026",
    CGV: "AV",
    footerLegalNotices: "Juridische kennisgeving",
    optimizeForChrome: "Website geoptimaliseerd voor Google Chrome",
    titleOfTheWork: "Zij kozen voor InventoZen",
    artistFirstPage: "Jean MIOTTE",
    "login.twoFactorTitle": "Voer de code in",
    "login.twoFactorDescription":
      "We hebben zojuist een verificatiecode naar uw e-mailadres gestuurd",
    "login.twoFactorExpired": "De code is verlopen",
    "login.twoFactorResendAsk": "Niets ontvangen?",
    "login.twoFactorResendCTA": "Code opnieuw verzenden",
    "login.invalid2FAToken": "Ongeldige code",
    "login.invalidCredentials": "Ongeldige gebruikersnaam of wachtwoord",
    "login.invalidCredentials.title": "Inloggen mislukt",
    "login.invalidCredentials.description":
      "Ongeldige gebruikersnaam of wachtwoord",
    "login.success.title": "Succesvol ingelogd",
    "login.success.description": " ",
    "login.error": "Er is een fout opgetreden",
  },
};
